import React, { type ElementRef, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  Platform,
  TextInput as RnTextInput,
  TextInputProps as RnTextInputProps,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import { Text } from '@fhs/ui';
import { useMergedEventHandlers } from '@fhs/utils';

import { tokens } from '../..//tokens';

import { InputContainer } from './input-container';

export type FormControlTextInputProps = Omit<
  RnTextInputProps,
  'editable' | 'placeholderTextColor'
> & {
  readOnly?: boolean;
  inputContainerStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  focusedStyle?: RnTextInputProps['style'];
  hoveredStyle?: RnTextInputProps['style'];
  readOnlyStyle?: RnTextInputProps['style'];
  leftIcon?: React.JSX.Element;
  rightIcon?: React.JSX.Element;
  label?: string;
  error?: string | boolean;
  required?: boolean;
};

export const FormControlTextInput = forwardRef<RnTextInput, FormControlTextInputProps>(
  function FormControlTextInput(
    { label, leftIcon, rightIcon, inputContainerStyle, labelStyle, required, ...props },
    forwardedRef
  ) {
    const inputRef = useRef<ElementRef<typeof RnTextInput> | null>(null);
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    const [internalText, setInternalText] = useState(props.defaultValue ?? '');

    const handleOnFocus = useMergedEventHandlers(props.onFocus, () => {
      setFocused(true);
      // On web, tabbing into a filled field auto-selects the whole value, which
      // makes screen readers (e.g. Windows Narrator) re-announce the value as
      // "selected". Collapse the selection to the end so it is read once. We
      // only do this when the full value is selected (keyboard Tab) to avoid
      // overriding a caret placed by a mouse click.
      if (Platform.OS === 'web') {
        const node = inputRef.current as unknown as HTMLInputElement | null;
        requestAnimationFrame(() => {
          if (!node || document.activeElement !== node) {
            return;
          }
          const length = node.value?.length ?? 0;
          if (length > 0 && node.selectionStart === 0 && node.selectionEnd === length) {
            try {
              node.setSelectionRange(length, length);
            } catch {
              // Some input types do not support selection ranges; ignore.
            }
          }
        });
      }
    });
    const handleOnBlur = useMergedEventHandlers(props.onBlur, () => setFocused(false));
    const handleChangeText = (text: string) => {
      setInternalText(text);
      props.onChangeText?.(text);
    };

    // On web the native `placeholder` attribute is announced by screen readers
    // even when the field is empty. To keep the placeholder purely visual, we
    // omit it from the input and render an aria-hidden overlay instead.
    const placeholderText = typeof props.placeholder === 'string' ? props.placeholder : undefined;
    const currentValue = props.value !== undefined ? props.value : internalText;
    const showWebPlaceholder =
      Platform.OS === 'web' && Boolean(placeholderText) && !currentValue;

    useImperativeHandle(forwardedRef, () => inputRef.current!);

    return (
      <>
        {label && (
          <Text weight="semibold" style={[styles.label, labelStyle]}>
            {label}
            {required && <Text style={styles.required}> *</Text>}
          </Text>
        )}
        <InputContainer
          style={
            [
              styles.inputContainer,
              inputContainerStyle,
              hovered && [styles.hovered, props.hoveredStyle],
              focused && [styles.focused, props.focusedStyle],
              props.readOnly && [styles.readOnly, props.readOnlyStyle],
              Boolean(props.error) && [styles.error],
              props.style,
            ] as ViewStyle[]
          }
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
        >
          {leftIcon &&
            React.cloneElement(leftIcon, {
              'aria-hidden': true,
              accessibilityElementsHidden: true,
              importantForAccessibility: 'no-hide-descendants',
            })}
          <View style={styles.inputWrapper}>
            {showWebPlaceholder && (
              <Text
                aria-hidden
                importantForAccessibility="no-hide-descendants"
                numberOfLines={1}
                pointerEvents="none"
                style={styles.placeholderOverlay}
              >
                {placeholderText}
              </Text>
            )}
            <RnTextInput
              {...props}
              placeholder={Platform.OS === 'web' ? undefined : props.placeholder}
              readOnly={props.readOnly}
              onChangeText={handleChangeText}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              placeholderTextColor={tokens.colors.$placeholderText}
              style={[styles.baseline, props.style]}
              ref={inputRef}
              accessibilityLabel={props.accessibilityLabel ?? label}
              accessibilityHint={
                props.accessibilityHint ??
                (typeof props.placeholder === 'string' ? props.placeholder : undefined)
              }
            />
          </View>
          {rightIcon && rightIcon}
        </InputContainer>
        {props.error && <Text style={styles.errorMessage}>{props.error}</Text>}
      </>
    );
  }
);

const styles = StyleSheet.create({
  required: {
    color: tokens.colors.$error,
    fontSize: 14,
    fontWeight: 'bold',
    height: 16,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity30,
    backgroundColor: tokens.colors.$white,
    minHeight: 48,
    paddingHorizontal: 16,
    flex: 1, // TextInput takes available space
  },
  inputWrapper: {
    flex: 1,
    justifyContent: 'center',
    ...(Platform.select({ web: { position: 'relative' } }) as object),
  },
  placeholderOverlay: {
    position: 'absolute',
    left: 6,
    right: 6,
    fontFamily: 'Montserrat',
    fontSize: 16,
    lineHeight: 20,
    color: tokens.colors.$placeholderText,
  },
  baseline: {
    fontFamily: 'Montserrat',
    fontSize: 16,
    lineHeight: 20,
    color: tokens.colors.$houseDark,
    paddingHorizontal: 6,
    flex: 1, // TextInput takes available space
    ...Platform.select({
      web: { outlineWidth: 0, outlineColor: 'transparent' }, // remove outline on web
    }),
  },

  pressed: {
    borderColor: tokens.colors.$houseDark,
  },

  focused: {
    borderColor: tokens.colors.$houseDark,
  },

  hovered: {
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  label: {
    marginBottom: 4,
  },
  readOnly: {
    color: tokens.colors.$disabledText,
    borderColor: tokens.colors.$blackOpacity30,
    backgroundColor: tokens.colors.$blackOpacity04,
    // web allows this, but react native changed their types
    ...(Platform.select({ web: { cursor: 'not-allowed' } }) as any),
  },
  error: {
    borderColor: tokens.colors.$error,
  },
  errorMessage: {
    color: tokens.colors.$error,
    fontSize: 12,
    height: 16,
  },
});
